import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/api";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserOutlined,
  ShopOutlined,
  AppstoreOutlined,
  ColumnWidthOutlined,
  ClockCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { Spin, Empty, message } from "antd";
import "./StallDetails.css";

const TABS = ["Details", "Description", "Location", "Facilities"];

function StallDetails() {
  const { slug } = useParams();
  const [stall, setStall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [imageErrors, setImageErrors] = useState({});
  const hasTrackedView = useRef(false);
  const stripRef = useRef(null);
  const [images, setImages] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  const handleNext = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev + 1) % otherImages.length);
    }
  }, [otherImages.length]);

  const handlePrev = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev === 0 ? otherImages.length - 1 : prev - 1));
    }
  }, [otherImages.length]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback((index, isMain = false) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
    if (isMain && images.length > 1) {
      const nextAvailableIndex = images.findIndex((_, idx) => !imageErrors[idx] && idx !== index);
      if (nextAvailableIndex !== -1) {
        setActiveImage(nextAvailableIndex);
      }
    }
  }, [images, imageErrors]);

  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      visitorId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("visitorId", visitorId);
    }
    return visitorId;
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const formatDate = (date) => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString();
  };

  const normalizePhone = (value) => value?.replace(/\D/g, "") || "";

  const getPriceLabel = (durationType) => {
    if (durationType === "daily") return "/day";
    if (durationType === "weekly") return "/week";
    if (durationType === "monthly") return "/month";
    return "";
  };

  const renderDetailRow = (label, value) =>
    value !== null && value !== undefined && value !== "" ? (
      <tr key={label}>
        <td className="stl-detail-label">{label}</td>
        <td className="stl-detail-value">{value}</td>
      </tr>
    ) : null;

  const renderImageWithFallback = (src, alt, className, index, isMain = false) => {
    if (imageErrors[index]) {
      return (
        <div className={`${className} stl-image-fallback`}>
          <span>Image not available</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(index, isMain)}
        loading="lazy"
      />
    );
  };

  useEffect(() => {
    const fetchStall = async () => {
      try {
        setLoading(true);
        let res;
        try {
          res = await API.get(`/stalls/slug/${slug}`);
        } catch (err) {
          try {
            res = await API.get(`/stalls/${slug}`);
          } catch (error) {
            console.log("Both slug and ID fetch failed:", error);
            setStall(null);
            message.error("Failed to load stall details");
            return;
          }
        }
        setStall(res.data);

        if (res.data?._id && !hasTrackedView.current) {
          hasTrackedView.current = true;
          try {
            const visitorId = getVisitorId();
            const viewRes = await API.post(
              `/stalls/${res.data._id}/view`,
              {},
              { headers: { "x-visitor-id": visitorId } }
            );
            setStall((prev) =>
              prev
                ? {
                  ...prev,
                  views:
                    typeof viewRes.data.views === "number"
                      ? viewRes.data.views
                      : prev.views,
                }
                : prev
            );
          } catch (viewError) {
            console.log("View count error:", viewError);
          }
        }
      } catch (err) {
        console.log(err);
        setStall(null);
        message.error("Error loading stall details");
      } finally {
        setLoading(false);
      }
    };

    hasTrackedView.current = false;
    setActiveImage(0);
    fetchStall();
  }, [slug]);

  useEffect(() => {
    if (stall) {
      const thumbnailOnly = stall.thumbnail ? [stall.thumbnail] : [];
      const otherImagesList = (stall.images || []).filter((img) => img !== stall.thumbnail);

      if (otherImagesList.length > 0) {
        setImages([otherImagesList[0]]);
        setOtherImages(otherImagesList);
      } else {
        setImages(thumbnailOnly.length > 0 ? thumbnailOnly : ["https://via.placeholder.com/800x600?text=No+Image"]);
        setOtherImages([]);
      }
      setImageErrors({});
      setActiveImage(0);
    }
  }, [stall]);

  useEffect(() => {
    if (previewOpen && stripRef.current) {
      const activeThumb = stripRef.current.children[previewIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [previewIndex, previewOpen]);

  useEffect(() => {
    if (previewOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [previewOpen]);

  if (loading) {
    return (
      <div className="stl-state">
        <Spin size="large" />
      </div>
    );
  }

  if (!stall) {
    return (
      <div className="stl-state">
        <Empty description="Stall not found" />
      </div>
    );
  }

  const phone = normalizePhone(stall.contactDetails?.phone);
  const durationType = stall.durationDetails?.durationType;
  const fullLocation = [
    stall.location?.address,
    stall.location?.city,
    stall.location?.state,
    stall.location?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const cacheKey = stall.updatedAt ? `?v=${encodeURIComponent(stall.updatedAt)}` : "";

  return (
    <div className="stl-root">
      {/* Gallery */}
      <section className="stl-gallery">
        <div
          className="stl-gallery__main"
          onClick={() => {
            setPreviewIndex(0);
            setPreviewOpen(true);
          }}
        >
          {otherImages.length > 0 ? (
            renderImageWithFallback(
              otherImages[activeImage],
              stall.title,
              "stl-gallery__main-img",
              activeImage,
              true
            )
          ) : (
            (images[0] || otherImages[0]) ? (
              renderImageWithFallback(
                images[0] || otherImages[0],
                stall.title,
                "stl-gallery__main-img",
                0,
                true
              )
            ) : (
              <div className="stl-image-fallback">
                <span>Image not available</span>
              </div>
            )
          )}
          <div className="stl-gallery__overlay" />
          <button
            className="stl-gallery__badge"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(0);
              setPreviewOpen(true);
            }}
            aria-label="View gallery"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {otherImages.length > 0 ? otherImages.length : 0} photo{otherImages.length !== 1 ? "s" : ""}
          </button>
        </div>

        {otherImages.length > 0 && (
          <div className="stl-gallery__strip">
            {otherImages.map((img, i) => (
              <button
                key={i}
                className={`stl-gallery__thumb${activeImage === i ? " stl-gallery__thumb--on" : ""
                  }`}
                onClick={() => setActiveImage(i)}
                aria-label={`View photo ${i + 1}`}
              >
                {renderImageWithFallback(
                  img,
                  `Thumbnail ${i + 1}`,
                  "stl-gallery__thumb-img",
                  i
                )}
                {i === 4 && otherImages.length > 5 && (
                  <span className="stl-gallery__more">
                    +{otherImages.length - 5}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Body */}
      <div className="stl-body">
        {/* Left Content */}
        <main className="stl-content">
          {/* Breadcrumb */}
          <nav className="stl-breadcrumb" aria-label="Breadcrumb">
            <span>Stalls</span>
            <span className="stl-bc-sep">/</span>
            <span>{stall.location?.city || "Listings"}</span>
            <span className="stl-bc-sep">/</span>
            <span className="stl-bc-active">{stall.title}</span>
          </nav>

          {/* Header */}
          <div className="stl-header">
            <div className="stl-chips">
              <span className="stl-chip stl-chip--type">
                {stall.stallDetails?.stallType || "Stall"}
              </span>
              <span
                className={`stl-chip stl-chip--status-${stall.status === "active" ? "active" : "inactive"
                  }`}
              >
                {stall.status}
              </span>
              {isAdmin && (
                <span
                  className={`stl-chip stl-chip--approval-${stall.approvalStatus}`}
                >
                  {stall.approvalStatus}
                </span>
              )}
              {stall.pricing?.negotiable && (
                <span className="stl-chip stl-chip--nego">Negotiable</span>
              )}
            </div>

            <h1 className="stl-title">{stall.title}</h1>

            {fullLocation && (
              <p className="stl-location">
                <EnvironmentOutlined />
                {fullLocation}
              </p>
            )}
          </div>

          {/* Stats Bar */}
          <div className="stl-stats">
            <div className="stl-stat">
              <span className="stl-stat__icon">
                <ColumnWidthOutlined />
              </span>
              <span className="stl-stat__val">{stall.stallDetails?.areaSqFt || 0} sqft</span>
              <span className="stl-stat__lbl">Area</span>
            </div>
            <div className="stl-stat__div" />

            <div className="stl-stat">
              <span className="stl-stat__icon">
                <ShopOutlined />
              </span>
              <span className="stl-stat__val">{stall.stallDetails?.stallType || "—"}</span>
              <span className="stl-stat__lbl">Stall Type</span>
            </div>
            <div className="stl-stat__div" />

            <div className="stl-stat">
              <span className="stl-stat__icon">
                <ClockCircleOutlined />
              </span>
              <span className="stl-stat__val">{durationType || "—"}</span>
              <span className="stl-stat__lbl">Duration</span>
            </div>
            <div className="stl-stat__div" />

            <div className="stl-stat">
              <span className="stl-stat__icon">
                <DollarOutlined />
              </span>
              <span className="stl-stat__val">{stall.pricing?.negotiable ? "Yes" : "No"}</span>
              <span className="stl-stat__lbl">Negotiable</span>
            </div>

            {isAdmin && (
              <>
                <div className="stl-stat__div" />
                <div className="stl-stat">
                  <span className="stl-stat__icon">
                    <EyeOutlined />
                  </span>
                  <span className="stl-stat__val">{stall.views || 0}</span>
                  <span className="stl-stat__lbl">Views</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="stl-tabs">
            <div className="stl-tabs__nav" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`stl-tabs__btn${activeTab === tab ? " stl-tabs__btn--on" : ""
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="stl-tabs__panel">
              {activeTab === "Details" && (
                <div className="stl-detail-grid">
                  <div className="stl-detail-card">
                    <h3 className="stl-detail-card__head">Stall Details</h3>
                    <table className="stl-detail-table">
                      <tbody>
                        {isAdmin && renderDetailRow("Stall ID", stall._id)}
                        {renderDetailRow("Stall Type", stall.stallDetails?.stallType)}
                        {renderDetailRow("Area", stall.stallDetails?.areaSqFt ? `${stall.stallDetails.areaSqFt} sqft` : null)}
                        {renderDetailRow("Duration Type", durationType)}
                        {renderDetailRow("Start Date", stall.durationDetails?.startDate ? formatDate(stall.durationDetails.startDate) : null)}
                        {renderDetailRow("End Date", stall.durationDetails?.endDate ? formatDate(stall.durationDetails.endDate) : null)}
                        {renderDetailRow("Status", stall.status)}
                        {isAdmin && renderDetailRow("Approval Status", stall.approvalStatus)}
                        {isAdmin && renderDetailRow("Views", stall.views || 0)}
                        {isAdmin && renderDetailRow("Slug", stall.slug)}
                        {renderDetailRow(
                          "Allowed Business Types",
                          stall.stallDetails?.allowedBusinessTypes?.length
                            ? stall.stallDetails.allowedBusinessTypes.join(", ")
                            : null
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="stl-detail-card">
                    <h3 className="stl-detail-card__head">Pricing & Listing</h3>
                    <table className="stl-detail-table">
                      <tbody>
                        {renderDetailRow("Price", stall.pricing?.price ? `₹ ${stall.pricing.price.toLocaleString("en-IN")}` : null)}
                        {renderDetailRow("Duration", durationType)}
                        {renderDetailRow("Negotiable", stall.pricing?.negotiable ? "Yes" : "No")}
                        {isAdmin && renderDetailRow("Expiry Date", stall.expiryDate ? formatDate(stall.expiryDate) : null)}
                        {renderDetailRow("Posted On", formatDate(stall.createdAt))}
                        {isAdmin && renderDetailRow("Updated On", formatDate(stall.updatedAt))}
                        {isAdmin && renderDetailRow("Thumbnail", stall.thumbnail ? "Available" : "No")}
                        {isAdmin && renderDetailRow("Total Images", stall.images?.length || 0)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "Description" && (
                <div className="stl-desc-card">
                  <p className="stl-desc-text">
                    {stall.description || "No description available."}
                  </p>
                </div>
              )}

              {activeTab === "Location" && (
                <div className="stl-location-wrap">
                  <div className="stl-detail-grid">
                    <div className="stl-detail-card">
                      <h3 className="stl-detail-card__head">Address</h3>
                      <table className="stl-detail-table">
                        <tbody>
                          {renderDetailRow("Address", stall.location?.address)}
                          {renderDetailRow("Locality", stall.location?.locality)}
                          {renderDetailRow("City", stall.location?.city)}
                          {renderDetailRow("State", stall.location?.state)}
                          {renderDetailRow("Country", stall.location?.country)}
                          {renderDetailRow("Pincode", stall.location?.pincode)}
                        </tbody>
                      </table>
                    </div>
                    <div className="stl-detail-card">
                      <h3 className="stl-detail-card__head">Contact Info</h3>
                      <table className="stl-detail-table">
                        <tbody>
                          {renderDetailRow("Owner", stall.ownerId?.name)}
                          {isAdmin && renderDetailRow("Owner Email", stall.ownerId?.email)}
                          {renderDetailRow("Contact Name", stall.contactDetails?.contactName)}
                          {renderDetailRow("Phone", stall.contactDetails?.phone)}
                          {renderDetailRow("Contact Email", stall.contactDetails?.email)}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {stall.location?.coordinates?.coordinates && (
                    <div className="stl-map">
                      <iframe
                        width="100%"
                        height="360"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        title="Location Map"
                        src={`https://www.google.com/maps?q=${stall.location.coordinates.coordinates[1]},${stall.location.coordinates.coordinates[0]}&z=15&output=embed`}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Facilities" && (
                <div className="stl-facilities-card">
                  <h3 className="stl-detail-card__head">Facilities & Amenities</h3>
                  <div className="stl-facilities-list">
                    {stall.facilities?.length > 0 ? (
                      <div className="stl-facilities-grid">
                        {stall.facilities.map((item, index) => (
                          <div key={index} className="stl-facility-item">
                            <span className="stl-facility__dot" />
                            {item}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="stl-empty-msg">No facilities listed for this stall.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Notice */}
          {stall.approvalStatus === "rejected" && stall.rejectionReason && (
            <div className="stl-rejection">
              <strong>Rejection Reason</strong>
              <p>{stall.rejectionReason}</p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="stl-sidebar">
          <div className="stl-sidebar__card">
            <div className="stl-sidebar__price">
              <span className="stl-sidebar__amt">
                ₹ {stall.pricing?.price?.toLocaleString("en-IN") || 0}
              </span>
              <span className="stl-sidebar__per">
                {getPriceLabel(durationType)}
              </span>
            </div>

            <hr className="stl-sidebar__rule" />

            <div className="stl-sidebar__owner">
              <span className="stl-sidebar__avatar" aria-hidden="true">
                <UserOutlined />
              </span>
              <div className="stl-sidebar__owner-info">
                <span className="stl-sidebar__owner-name">
                  {stall.contactDetails?.contactName || stall.ownerId?.name || "Contact Owner"}
                </span>
                <span className="stl-sidebar__owner-role">Stall Owner</span>
              </div>
            </div>

            {(stall.contactDetails?.phone || stall.contactDetails?.email) && (
              <dl className="stl-sidebar__contact-list">
                {stall.contactDetails?.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{stall.contactDetails.phone}</dd>
                  </>
                )}
                {stall.contactDetails?.email && (
                  <>
                    <dt>Email</dt>
                    <dd className="stl-sidebar__email">{stall.contactDetails.email}</dd>
                  </>
                )}
              </dl>
            )}

            {phone && (
              <div className="stl-sidebar__actions">
                <a href={`tel:${phone}`} className="stls-btn stls-btn--call">
                  <PhoneOutlined style={{ transform: "rotate(90deg)" }} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="stls-btn stls-btn--wa"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            )}

            <hr className="stl-sidebar__rule" />

            <dl className="stl-sidebar__facts">
              <div className="stl-sidebar__fact">
                <dt>Area</dt>
                <dd>{stall.stallDetails?.areaSqFt || 0} sqft</dd>
              </div>
              <div className="stl-sidebar__fact">
                <dt>Stall Type</dt>
                <dd>{stall.stallDetails?.stallType || "—"}</dd>
              </div>
              <div className="stl-sidebar__fact">
                <dt>Duration</dt>
                <dd>{durationType || "—"}</dd>
              </div>
              <div className="stl-sidebar__fact">
                <dt>Negotiable</dt>
                <dd>{stall.pricing?.negotiable ? "Yes" : "No"}</dd>
              </div>
              <div className="stl-sidebar__fact">
                <dt>Posted</dt>
                <dd>
                  {stall.createdAt
                    ? new Date(stall.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <div className="stl-sidebar__admin">
                <span className="stl-sidebar__admin-label">
                  Listed by (Admin)
                </span>
                <span>{stall.ownerId?.name || "N/A"}</span>
                <span>{stall.ownerId?.email || "N/A"}</span>
              </div>
            )}
          </div>

          <p className="stl-sidebar__note">
            Listed on{" "}
            {stall.createdAt
              ? new Date(stall.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
              : "—"}
          </p>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      {phone && (
        <div
          className="stl-mobile-bar"
          role="complementary"
          aria-label="Contact actions"
        >
          <div className="stl-mobile-bar__price">
            <span className="stl-mobile-bar__amt">
              ₹ {stall.pricing?.price?.toLocaleString("en-IN") || 0}
            </span>
            <span className="stl-mobile-bar__per">
              {getPriceLabel(durationType)}
            </span>
          </div>
          <div className="stl-mobile-bar__btns">
            <a href={`tel:${phone}`} className="stl-mbtn stl-mbtn--call">
              <PhoneOutlined style={{ transform: "rotate(90deg)" }} /> Call
            </a>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="stl-mbtn stl-mbtn--wa"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewOpen && (
        <div
          className="stl-lb"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="stl-lb__blur"
            style={{ backgroundImage: `url(${otherImages[previewIndex] || images[0]})` }}
          />

          <button
            className="stl-lb__close"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
          >
            ✕
          </button>

          <span className="stl-lb__counter">
            {previewIndex + 1} / {otherImages.length}
          </span>

          <div className="stl-lb__stage" onClick={(e) => e.stopPropagation()}>
            <button
              className="stl-lb__nav stl-lb__nav--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              ‹
            </button>
            {renderImageWithFallback(
              otherImages[previewIndex],
              `Preview ${previewIndex + 1}`,
              "stl-lb__img",
              previewIndex
            )}
            <button
              className="stl-lb__nav stl-lb__nav--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          {otherImages.length > 1 && (
            <div
              className="stl-lb__strip"
              ref={stripRef}
              onClick={(e) => e.stopPropagation()}
            >
              {otherImages.map((img, i) => (
                <button
                  key={i}
                  className={`stl-lb__thumb${previewIndex === i ? " stl-lb__thumb--on" : ""
                    }`}
                  onClick={() => setPreviewIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                >
                  {renderImageWithFallback(img, `Thumbnail ${i + 1}`, "", i)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StallDetails;